import { useCallback, useEffect, useRef, useState } from "react";
import api from "../api/axios";
import { logError } from "../api/logger";

const STORAGE_KEY = "selectedCity";

// Single source of truth for "which city is the visitor browsing" used
// by the catalog header dropdown. The selection is persisted to
// localStorage and ALSO broadcast through `storage` events so two
// open tabs stay in sync. On the very first visit (no stored city) we
// silently call /api/geo/country to pre-fill a sensible default.
//
// The hook owns three pieces of state:
//   cities          — full list from /api/places/cities
//   selectedCity    — what the dropdown shows
//   geoLoaded       — true once the initial geo guess has resolved or
//                     we decided not to bother (i.e. localStorage hit)
function readStoredCity() {
  try {
    return localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

function writeStoredCity(city) {
  try {
    if (city) localStorage.setItem(STORAGE_KEY, city);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — Safari private mode etc.
  }
}

export default function useCitySelection() {
  const [cities, setCities] = useState([]);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [selectedCity, setSelectedCity] = useState(() => readStoredCity());
  const [geoLoaded, setGeoLoaded] = useState(() => Boolean(readStoredCity()));
  // Country code from the geo guess — surfaced so callers can decide
  // whether to show the "no places in your country" banner.
  const [country, setCountry] = useState(null);
  const initialisedRef = useRef(false);

  // Cities don't change often; one fetch per session is plenty. The
  // backend already caches with a 5-minute Cache-Control so a refresh
  // hits the cache, not the database.
  useEffect(() => {
    let cancelled = false;
    api
      .get("/places/cities")
      .then((res) => {
        if (!cancelled) {
          setCities(Array.isArray(res.data) ? res.data : []);
          setCitiesLoaded(true);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setCitiesLoaded(true);
          logError("Failed to load cities", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Geo guess runs exactly once per session, and only when the visitor
  // hasn't already picked a city. We deliberately do NOT block on it —
  // the dropdown stays empty until the response arrives, then the
  // suggested city slides in.
  useEffect(() => {
    if (initialisedRef.current) return;
    initialisedRef.current = true;
    if (selectedCity) return;
    api
      .get("/geo/country", { silent: true })
      .then((res) => {
        const data = res.data || {};
        setCountry(data.country || null);
        if (data.suggestedCity) {
          setSelectedCity(data.suggestedCity);
          writeStoredCity(data.suggestedCity);
        }
      })
      .catch((err) => {
        logError("Geo country lookup failed", err);
      })
      .finally(() => setGeoLoaded(true));
  }, [selectedCity]);

  // Cross-tab sync: when another tab updates the selection, mirror it.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setSelectedCity(e.newValue || "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const selectCity = useCallback((city) => {
    setSelectedCity(city || "");
    writeStoredCity(city || "");
  }, []);

  // True when the geo guess reported a country but our DB has no place
  // in it (suggestedCity ends up null/falls back to the busiest city,
  // which is a different country). Drives the banner asking the user
  // to pick another city.
  const noPlacesInCountry =
    geoLoaded &&
    country !== null &&
    citiesLoaded &&
    cities.length > 0 &&
    !cities.some((c) => c.city.toLowerCase() === (selectedCity || "").toLowerCase());

  return {
    cities,
    citiesLoaded,
    selectedCity,
    selectCity,
    country,
    geoLoaded,
    noPlacesInCountry,
  };
}
